/* eslint-disable @nfq/no-magic-numbers */
import {spacing} from '@nfq/react-grid';

import type {Theme} from '@emotion/react';

/**
 * Generating the appropriate size for the upload icon based on the provided size prop.
 *
 * @param size The size prop can be either 'large' or 'small'. If the size is 'large', the function returns 32, which represents the height of the upload icon in pixels. If the size is 'small', it returns 20, indicating a smaller height for the upload icon.
 * @returns The function returns a number representing the height of the upload icon in pixels, which can be used to set the dimensions of the icon in the file input component.
 */
export const getUploadIconSize = (size: 'large' | 'small') => (size === 'large' ? 32 : 20);

/**
 * Converts a file size in bytes to a human-readable string format (e.g., KB, MB, GB).
 *
 * @param bytes The file size in bytes that needs to be converted into a human-readable format.
 *
 * @returns A string representing the file size in a more understandable format, such as "500 B", "1.23 KB", "4.56 MB", or "7.89 GB".
 */
export const fileSizeStringFromBytes = (bytes: number) => {
    if (bytes < 1024) {
        return `${bytes} B`;
    } else if (bytes < 1024 * 1024) {
        return `${(bytes / 1024)} KB`;
    } else if (bytes < 1024 * 1024 * 1024) {
        return `${(bytes / (1024 * 1024))} MB`;
    }

    return `${(bytes / (1024 * 1024 * 1024))} GB`;
};

interface FileInputVariablesProps {
    theme: Theme;
}

/**
 * Generates CSS variables for the default file input styling.
 *
 * @param params       The parameters for generating file input variables.
 * @param params.theme The theme object containing color values.
 * @returns A string containing CSS variable declarations for default file input styling.
 */
export const getFileInputVariables = ({theme}: FileInputVariablesProps) => `
    --file-input-background-color: ${theme.colors.neutral20};
    --file-input-border-color: ${theme.colors.neutral20};
    --file-input-font-color: ${theme.colors.textSecondary};
    --file-input-padding: ${spacing(8)} 0;
    --file-input-flex-direction: column;
    --label-font-color: ${theme.colors.textPrimary};
    --cursor: pointer;

    &[data-size="small"] {
        --file-input-padding: ${spacing(4)} 0;
        --file-input-flex-direction: row;
    }
`;

/**
 * Generates CSS variables for the disabled state file input styling.
 *
 * @param props       The parameters for generating disabled file input variables.
 * @param props.theme The theme object containing color values.
 * @returns A string containing CSS variable declarations for disabled state file input styling.
 */
export const getFileInputDisabledVariables = ({theme}: FileInputVariablesProps) => `
    --file-input-background-color: ${theme.colors.neutral20};
    --file-input-border-color: ${theme.colors.neutral20};
    --file-input-font-color: ${theme.colors.neutral40};
    --label-font-color: ${theme.colors.neutral60};
    --cursor: not-allowed;
`;

/**
 * Generates CSS variables for the error state file input styling.
 *
 * @param params       The parameters for generating error file input variables.
 * @param params.theme The theme object containing color values.
 * @returns A string containing CSS variable declarations for error state file input styling.
 */
export const getFileInputErrorVariables = ({theme}: FileInputVariablesProps) => `
    --file-input-border-color: ${theme.colors.error};
    --label-font-color: ${theme.colors.textPrimary};
`;

/**
 * Generates CSS variables for the focus state file input styling.
 *
 * @param props       The parameters for generating focused file input variables.
 * @param props.theme The theme object containing color values.
 * @returns A string containing CSS variable declarations for focused state file input styling, including background, border, and label font colors.
 */
export const getFileInputFocusVariables = ({theme}: FileInputVariablesProps) => `
    --file-input-background-color: ${theme.colors.neutral0};
    --file-input-border-color: ${theme.colors.primary};
    --label-font-color: ${theme.colors.primary};
`;